/**
 * VoiceMemo.js
 *
 * Records a short voice message for the birthday boy and saves it to the backend.
 * Uses the browser's MediaRecorder, which records MP4 on iPhone Safari and WebM elsewhere.
 * The guest can listen back before saving, record again, or delete it.
 */

import React, { useEffect, useRef, useState } from "react";
import { Mic, Pause, Play, RotateCcw, Square, Trash2 } from "lucide-react";
import { deleteVoiceMemo, uploadVoiceMemo } from "../helpers/flyApi";

const MAX_SECONDS = 120;
const INK = "#1B2A41";
const PAPER = "#FBF7EF";
const MUTED = "#5B6577";
const ERROR = "#A8183D";
const MONO = "'JetBrains Mono', monospace";

const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;

/**
 * The best format this browser can record: MP4 (Safari) or WebM/Ogg (Chrome, Firefox)
 */
const recordingType = () => {
    if (typeof MediaRecorder === "undefined") return null;
    return ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"].find((type) => MediaRecorder.isTypeSupported?.(type)) || "";
};

/**
 * Plays a recording with a single round button
 */
const PlayButton = ({ url, accent }) => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const toggle = () => {
        if (playing) audioRef.current.pause();
        else audioRef.current.play().catch(() => setPlaying(false));
    };
    return (
        <>
            <audio ref={audioRef} src={url} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} preload="auto" />
            <button type="button" onClick={toggle} aria-label={playing ? "Pause" : "Play your message"} className="h-14 w-14 shrink-0 rounded-full inline-flex items-center justify-center" style={{ background: accent, color: PAPER }}>
                {playing ? <Pause size={22} aria-hidden="true" /> : <Play size={22} aria-hidden="true" />}
            </button>
        </>
    );
};

/**
 * @param {Object} booking      -   The guest's booking (voiceMemoAt is set once one is saved)
 * @param {Function} onSaved    -   Called with the updated booking after saving or deleting
 * @returns {JSX.Element}
 */
const VoiceMemo = ({ booking, onSaved }) => {
    const accent = booking.pass.colour;
    // 'idle' | 'recording' | 'review' (recorded, not saved) | 'saving'
    const [state, setState] = useState("idle");
    const [seconds, setSeconds] = useState(0);
    const [recording, setRecording] = useState(null);
    const [error, setError] = useState("");
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const timerRef = useRef(null);
    const saved = !!booking.voiceMemoAt;

    const stopTracks = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        clearInterval(timerRef.current);
    };

    // Let go of the microphone and the recording's memory when leaving
    useEffect(() => () => stopTracks(), []);
    useEffect(() => () => { if (recording) URL.revokeObjectURL(recording.url); }, [recording]);

    const start = async () => {
        setError("");
        const type = recordingType();
        if (type === null || !navigator.mediaDevices?.getUserMedia) {
            setError("This browser can't record audio. Try Safari on iPhone or Chrome on Android.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const recorder = new MediaRecorder(stream, { ...(type ? { mimeType: type } : {}), audioBitsPerSecond: 64000 });
            const chunks = [];
            recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
            recorder.onstop = () => {
                stopTracks();
                const blob = new Blob(chunks, { type: recorder.mimeType || type || "audio/webm" });
                setRecording({ blob, url: URL.createObjectURL(blob) });
                setState("review");
            };
            recorderRef.current = recorder;
            recorder.start(1000);
            const startedAt = Date.now();
            setSeconds(0);
            setState("recording");
            timerRef.current = setInterval(() => {
                const elapsed = (Date.now() - startedAt) / 1000;
                setSeconds(elapsed);
                if (elapsed >= MAX_SECONDS && recorder.state === "recording") recorder.stop();
            }, 250);
        } catch (err) {
            stopTracks();
            setError(err.name === "NotAllowedError"
                ? "Microphone access was blocked. Allow it for this site in your browser's settings, then try again."
                : "Couldn't start recording. Please try again.");
        }
    };

    const stop = () => {
        if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    };

    const save = async () => {
        setState("saving");
        setError("");
        try {
            onSaved(await uploadVoiceMemo(booking.pass.pnr, recording.blob));
            setRecording(null);
            setState("idle");
        } catch (err) {
            setError(err.message || "Couldn't save your message. Please try again.");
            setState("review");
        }
    };

    const remove = async () => {
        setError("");
        try {
            onSaved(await deleteVoiceMemo(booking.pass.pnr));
        } catch (err) {
            setError(err.message || "Couldn't delete your message.");
        }
    };

    const roundButton = "h-20 w-20 rounded-full inline-flex items-center justify-center transition hover:opacity-90 disabled:opacity-50";

    return (
        <div className="flex flex-col items-center gap-5 text-center">
            {state === "recording" && (
                <>
                    <p className="text-4xl font-bold" style={{ fontFamily: MONO, color: accent }}>{clock(seconds)}</p>
                    <p className="text-xs tracking-[0.2em] animate-pulse" style={{ color: ERROR }}>● RECORDING · UP TO {clock(MAX_SECONDS)}</p>
                    <button type="button" onClick={stop} aria-label="Stop recording" className={roundButton} style={{ background: ERROR, color: PAPER }}>
                        <Square size={26} fill={PAPER} aria-hidden="true" />
                    </button>
                </>
            )}

            {(state === "review" || state === "saving") && recording && (
                <>
                    <div className="flex items-center gap-4">
                        <PlayButton url={recording.url} accent={accent} />
                        <div className="text-left">
                            <p className="font-bold">Your message · {clock(seconds)}</p>
                            <p className="text-sm" style={{ color: MUTED }}>Listen back, then send it to the black box.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3">
                        <button type="button" onClick={save} disabled={state === "saving"} className="rounded-full px-6 py-3 text-sm font-bold tracking-[0.16em] disabled:opacity-50" style={{ background: accent, color: PAPER }}>
                            {state === "saving" ? "SAVING…" : "SAVE MESSAGE"}
                        </button>
                        <button type="button" onClick={start} disabled={state === "saving"} className="inline-flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-sm font-bold tracking-[0.12em] disabled:opacity-50" style={{ borderColor: INK }}>
                            <RotateCcw size={16} aria-hidden="true" /> RECORD AGAIN
                        </button>
                    </div>
                </>
            )}

            {state === "idle" && saved && (
                <>
                    <p className="rounded-full px-4 py-2 text-sm font-bold tracking-[0.12em]" style={{ background: `${accent}1a`, color: accent }}>
                        ✓ MESSAGE SAVED IN THE BLACK BOX
                    </p>
                    {booking.checkInOpen && (
                        <div className="flex flex-wrap justify-center gap-3">
                            <button type="button" onClick={start} className="inline-flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-sm font-bold tracking-[0.12em]" style={{ borderColor: INK }}>
                                <Mic size={16} aria-hidden="true" /> RECORD A NEW ONE
                            </button>
                            <button type="button" onClick={remove} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold tracking-[0.12em]" style={{ color: ERROR }}>
                                <Trash2 size={16} aria-hidden="true" /> DELETE
                            </button>
                        </div>
                    )}
                </>
            )}

            {state === "idle" && !saved && (
                <>
                    <button type="button" onClick={start} aria-label="Start recording" className={roundButton} style={{ background: accent, color: PAPER, boxShadow: `0 0 0 8px ${accent}26` }}>
                        <Mic size={32} aria-hidden="true" />
                    </button>
                    <p className="text-xs tracking-[0.2em]" style={{ color: MUTED }}>TAP TO RECORD · UP TO 2 MINUTES</p>
                </>
            )}

            {error && <p role="alert" className="text-sm max-w-sm" style={{ color: ERROR }}>{error}</p>}
        </div>
    );
};

export default VoiceMemo;
